import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAcademyApi, academyStudentApi, adminLearningScheduleApi, adminStudentApi } from '@/services/api';
import { message } from 'antd';
import { 
  Card, 
  Typography, 
  Select, 
  Input, 
  Row, 
  Col,
  Button,
  Modal,
  Checkbox,
  Pagination,
} from 'antd';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { DayPilot, DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import AdminLearningGradingList from './grading/list';
import './timeline.css';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface Student {
  id: string;
  name: string;
  birthDate: string;
  academy: string;
  academyName: string;
  loginId?: string;
}

interface LearningEvent {
  id: string;
  title: string;
  start: string;
  endDate: string;
  end: string;
  resourceId: string;
  studentName: string;
  birthDate: string;
  academyId?: number | null;
  academyCode?: string;
  backgroundColor?: string;
  borderColor?: string;
  extendedProps?: Record<string, any>;
  groupUuid?: string;
}



interface Resource {
  id: string;
  title: string;
  extendedProps: {
    birthDate: string;
    academy: string;
    academyName: string;
    loginId?: string;
  };
}

interface AcademyOption {
  value: string;
  label: string;
  academyId?: number;
  academyCode?: string;
}

const DEFAULT_EVENT_COLOR = '#4f46e5';

const parseColorToRGB = (color?: string): { r: number; g: number; b: number } | null => {
  if (!color) return null;
  const hexMatch = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hexValue = hexMatch[1];
    if (hexValue.length === 3) {
      hexValue = hexValue.split('').map((char) => `${char}${char}`).join('');
    }
    const intValue = parseInt(hexValue, 16);
    return {
      r: (intValue >> 16) & 255,
      g: (intValue >> 8) & 255,
      b: intValue & 255,
    };
  }

  const rgbMatch = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
};

const getReadableFontColor = (color?: string): string | undefined => {
  const rgb = parseColorToRGB(color);
  if (!rgb) return undefined;
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness < 145 ? '#f8fafc' : '#0f172a';
};

const AdminLearningManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const view = new URLSearchParams(location.search).get('view');
  const [selectedAcademy, setSelectedAcademy] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [events, setEvents] = useState<LearningEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<LearningEvent[]>([]);
  const rangeDefaults = useMemo(() => {
    const startOfMonth = dayjs().startOf('month');
    const endOfMonth = startOfMonth.endOf('month');
    return {
      cursor: startOfMonth.format('YYYY-MM-DD'),
      start: startOfMonth.format('YYYY-MM-DD'),
      end: endOfMonth.format('YYYY-MM-DD'),
    };
  }, []);
  const [rangeCursor, setRangeCursor] = useState<string>(rangeDefaults.cursor);
  const [rangeStart, setRangeStart] = useState<string>(rangeDefaults.start);
  const [rangeEnd, setRangeEnd] = useState<string>(rangeDefaults.end);
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  
  // Pagination State
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalResources, setTotalResources] = useState<number>(0);

  const [academyOptions, setAcademyOptions] = useState<AcademyOption[]>([{ value: 'all', label: '전체' }]);
  // 복사 모달 상태
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyScheduleId, setCopyScheduleId] = useState<string | null>(null);
  const [copyCandidates, setCopyCandidates] = useState<Resource[]>([]);
  const [copySelected, setCopySelected] = useState<string[]>([]);
  const [scheduleReloadSeq, setScheduleReloadSeq] = useState(0);
  const [resourcesVersion, setResourcesVersion] = useState(0);
  const schedulerRef = useRef<DayPilot.Scheduler | null>(null);
  const baselineEventsRef = useRef<Record<string, { start: string; endDate: string }>>({});
  const [pendingAdjustments, setPendingAdjustments] = useState<Record<string, { start?: string; endDate?: string }>>({});
  const [pendingCopies, setPendingCopies] = useState<{ sourceScheduleId: string; targetStudentUserIds: number[] }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [rowHeaderWidth, setRowHeaderWidth] = useState<number>(260);
  const [isRowHeaderResizing, setIsRowHeaderResizing] = useState(false);
  const rowHeaderResizeSnapshot = useRef<{ startX: number; startWidth: number } | null>(null);
  const middlePanSnapshotRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    schedulerScrollX: number;
    schedulerScrollY: number;
  } | null>(null);
  const [isMiddlePanning, setIsMiddlePanning] = useState(false);
  const schedulerContainerRef = useRef<HTMLDivElement | null>(null);
  const rowHeaderWidthRounded = Math.round(rowHeaderWidth);
  const ROW_HEADER_MIN_WIDTH = 160;
  const ROW_HEADER_MAX_WIDTH = 420;

  const pendingAdjustmentCount = Object.keys(pendingAdjustments).length;
  const pendingCopyCount = pendingCopies.reduce((sum, entry) => sum + entry.targetStudentUserIds.length, 0);
  const hasPendingChanges = pendingAdjustmentCount > 0 || pendingCopyCount > 0;
  const daysInRange = useMemo(
    () => Math.max(1, dayjs(rangeEnd).diff(rangeStart, 'day') + 1),
    [rangeStart, rangeEnd]
  );
  const todayRef = useMemo(() => dayjs().startOf('day'), []);
  const toSchedulerDate = useCallback((value: string) => (
    value.includes('T') ? value : `${value}T00:00:00`
  ), []);

  const overdueInfo = useMemo(() => {
    const eventMap: Record<string, boolean> = {};
    const resourceSet = new Set<string>();
    filteredEvents.forEach(ev => {
      const endDate = ev.endDate || ev.end || ev.start;
      if (!endDate) return;
      const isOverdue = dayjs(endDate).isBefore(todayRef, 'day');
      if (!isOverdue) return;
      eventMap[ev.id] = true;
      if (ev.resourceId) {
        resourceSet.add(String(ev.resourceId));
      }
    });
    return { eventMap, resourceSet };
  }, [filteredEvents, todayRef]);

  const schedulerEvents = useMemo<DayPilot.EventData[]>(() => (
    filteredEvents.map((ev) => {
      const startIso = toSchedulerDate(ev.start);
      const exclusiveEnd = ev.end || dayjs(ev.endDate).add(1, 'day').format('YYYY-MM-DD');
      const endIso = toSchedulerDate(exclusiveEnd);
      
      const classNames = [];
      const isOverdue = overdueInfo.eventMap[ev.id];
      if (isOverdue) {
        classNames.push('scheduler-event-overdue');
      }

      const baseColor = ev.backgroundColor || DEFAULT_EVENT_COLOR;
      const backColor = isOverdue ? 'rgba(248, 113, 113, 0.28)' : baseColor;
      const borderColor = isOverdue ? 'transparent' : 'rgba(15, 23, 42, 0.08)';
      const fontColor = isOverdue ? '#7f1d1d' : getReadableFontColor(backColor);

      return {
        id: ev.id,
        text: ev.title,
        start: startIso,
        end: endIso,
        resource: ev.resourceId,
        backColor,
        borderColor,
        fontColor,
        cssClass: classNames.length ? classNames.join(' ') : undefined,
        tags: {
          academyId: ev.academyId ?? null,
          academyCode: ev.academyCode ?? null,
          endDate: ev.endDate,
          extendedProps: ev.extendedProps,
        },
      };
    })
  ), [filteredEvents, overdueInfo, toSchedulerDate]);

  const schedulerResources = useMemo<DayPilot.ResourceData[]>(() => (
    filteredResources.map((res) => ({
      id: res.id,
      name: res.title,
      tags: res.extendedProps,
      cssClass: overdueInfo.resourceSet.has(String(res.id)) ? 'scheduler-resource-overdue' : undefined,
    }))
  ), [filteredResources, overdueInfo]);
  const timeHeaders = useMemo<DayPilot.TimeHeaderData[]>(() => ([
    { groupBy: 'Month', format: viewMode === 'year' ? 'MMM' : 'MMMM' },
    { groupBy: 'Day', format: 'd' },
  ]), [viewMode]);
  const cellWidth = viewMode === 'year' ? 18 : 40;

  const markEventDirty = useCallback((eventId: string, nextValues: { start?: string; endDate?: string }) => {
    setPendingAdjustments(prev => {
      const baseline = baselineEventsRef.current[eventId];
      if (!baseline) return prev;
      const updated = { ...prev };
      const existing = { ...(updated[eventId] || {}) };

      if (nextValues.start !== undefined) {
        if (nextValues.start !== baseline.start) {
          existing.start = nextValues.start;
        } else {
          delete existing.start;
        }
      }

      if (nextValues.endDate !== undefined) {
        if (nextValues.endDate !== baseline.endDate) {
          existing.endDate = nextValues.endDate;
        } else {
          delete existing.endDate;
        }
      }

      if (!Object.keys(existing).length) {
        delete updated[eventId];
      } else {
        updated[eventId] = existing;
      }

      return updated;
    });
  }, []);

  const applyRange = useCallback((cursor: dayjs.Dayjs, mode: 'month' | 'year') => {
    const start = cursor.startOf(mode === 'month' ? 'month' : 'year');
    const end = cursor.endOf(mode === 'month' ? 'month' : 'year');
    const startStr = start.format('YYYY-MM-DD');
    const endStr = end.format('YYYY-MM-DD');
    setRangeCursor(startStr);
    setRangeStart(startStr);
    setRangeEnd(endStr);
  }, []);

  const isSchedulerBackgroundTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    if (target.closest('.scheduler-row-header-resizer')) return false;
    if (target.closest('.scheduler_default_event, .scheduler_default_event_inner')) return false;
    if (target.closest('.scheduler_default_rowheader, .scheduler_default_rowheader_inner')) return false;
    if (target.closest('button, a, input, textarea, select, [role="button"]')) return false;
    return Boolean(
      target.closest(
        '.scheduler_default_cell, .scheduler_default_cell_inner, .scheduler_default_timeheader, .scheduler_default_timeheader_cell, .scheduler_default_timeheader_cell_inner, .scheduler_default_main'
      )
    );
  }, []);

  const handleSchedulerContainerMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const isMiddleButton = event.button === 1;
    const isLeftButtonOnBackground = event.button === 0 && isSchedulerBackgroundTarget(event.target);
    if (!isMiddleButton && !isLeftButtonOnBackground) return;
    event.preventDefault();
    event.stopPropagation();
    const container = schedulerContainerRef.current;
    const scheduler = schedulerRef.current;
    if (!container || !scheduler) return;
    middlePanSnapshotRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      schedulerScrollX: typeof scheduler.getScrollX === 'function' ? scheduler.getScrollX() : 0,
      schedulerScrollY: typeof scheduler.getScrollY === 'function' ? scheduler.getScrollY() : 0,
    };
    setIsMiddlePanning(true);
  }, [isSchedulerBackgroundTarget]);

  const handleRowHeaderResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    rowHeaderResizeSnapshot.current = {
      startX: event.clientX,
      startWidth: rowHeaderWidthRounded,
    };
    setIsRowHeaderResizing(true);
  }, [rowHeaderWidthRounded]);

  useEffect(() => {
    if (!isRowHeaderResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!rowHeaderResizeSnapshot.current) return;
      const delta = event.clientX - rowHeaderResizeSnapshot.current.startX;
      const nextWidth = Math.min(
        ROW_HEADER_MAX_WIDTH,
        Math.max(
          ROW_HEADER_MIN_WIDTH,
          rowHeaderResizeSnapshot.current.startWidth + delta
        )
      );
      setRowHeaderWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setIsRowHeaderResizing(false);
      rowHeaderResizeSnapshot.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isRowHeaderResizing]);

  useEffect(() => {
    setIsMiddlePanning(false);
    middlePanSnapshotRef.current = null;
  }, [view]);

  useEffect(() => {
    if (!isMiddlePanning) return;
    const handleMouseMove = (event: MouseEvent) => {
      const container = schedulerContainerRef.current;
      const scheduler = schedulerRef.current;
      const snapshot = middlePanSnapshotRef.current;
      if (!snapshot) return;
      if (!container) {
        setIsMiddlePanning(false);
        middlePanSnapshotRef.current = null;
        return;
      }
      const deltaX = event.clientX - snapshot.startX;
      const deltaY = event.clientY - snapshot.startY;

      if (container) {
        container.scrollLeft = snapshot.scrollLeft - deltaX;
        container.scrollTop = snapshot.scrollTop - deltaY;
      }

      if (scheduler) {
        const nextScrollX = Math.max(0, (snapshot.schedulerScrollX ?? 0) - deltaX);
        const nextScrollY = Math.max(0, (snapshot.schedulerScrollY ?? 0) - deltaY);
        if (typeof scheduler.setScrollX === 'function') {
          scheduler.setScrollX(nextScrollX);
        }
        if (typeof scheduler.setScrollY === 'function') {
          scheduler.setScrollY(nextScrollY);
        }
      }
    };
    const handleMouseUp = () => {
      setIsMiddlePanning(false);
      middlePanSnapshotRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMiddlePanning]);

  useEffect(() => {
    if (!isRowHeaderResizing && !isMiddlePanning) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const activeCursor = isRowHeaderResizing ? 'col-resize' : 'grabbing';
    document.body.style.cursor = activeCursor;
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isRowHeaderResizing, isMiddlePanning]);

  const handleShiftRange = useCallback((direction: number) => {
    setRangeCursor(prev => {
      const base = dayjs(prev).add(direction, viewMode === 'month' ? 'month' : 'year');
      const start = base.startOf(viewMode === 'month' ? 'month' : 'year');
      const end = base.endOf(viewMode === 'month' ? 'month' : 'year');
      const startStr = start.format('YYYY-MM-DD');
      const endStr = end.format('YYYY-MM-DD');
      setRangeStart(startStr);
      setRangeEnd(endStr);
      return startStr;
    });
  }, [viewMode]);

  const scrollSchedulerBy = useCallback((direction: -1 | 1) => {
    const container = schedulerContainerRef.current;
    if (!container) return;
    const scheduler = schedulerRef.current;
    const stepSize = Math.max(120, Math.round(container.clientWidth * 0.6));
    const currentX = scheduler && typeof scheduler.getScrollX === 'function'
      ? scheduler.getScrollX()
      : container.scrollLeft;
    const nextX = Math.max(0, currentX + stepSize * direction);
    container.scrollLeft = nextX;
    if (scheduler && typeof scheduler.setScrollX === 'function') {
      scheduler.setScrollX(nextX);
    }
  }, []);

  const handleToday = useCallback(() => {
    applyRange(dayjs(), viewMode);
  }, [applyRange, viewMode]);

  const handleViewModeChange = useCallback((mode: 'month' | 'year') => {
    if (mode === viewMode) return;
    setViewMode(mode);
    applyRange(dayjs(rangeCursor), mode);
  }, [applyRange, rangeCursor, viewMode]);

    // [Fix] 우클릭시 복사 대상자 필터링 제거 (학원단과 동일하게 수정)
    const openCopyModalForEvent = useCallback(async (eventId: string) => {
      try {
        const allowed: Resource[] = filteredResources;
        if (!allowed.length) {
          message.info('복사할 수 있는 대상 학생이 없습니다.');
          return;
        }
        setCopyCandidates(allowed);
        setCopySelected([]);
        setCopyScheduleId(eventId);
        setCopyModalVisible(true);
      } catch (_) {
        message.error('복사 대상 조회 중 오류가 발생했습니다.');
      }
    }, [filteredResources]);

    const handleEventMove = useCallback((args: DayPilot.SchedulerEventMoveArgs) => {
      const eventId = String(args.e.id());
      if (args.newResource && args.newResource !== args.e.resource()) {
        message.warning('다른 학생으로 이동은 불가합니다. 복사 기능을 이용하세요.');
        args.preventDefault();
        return;
      }
      // [Modified] Check against original start date from baseline to allow free movement before saving
      const baseline = baselineEventsRef.current[eventId];
      const originalStart = baseline ? dayjs(baseline.start).startOf('day') : dayjs(args.e.start().toString()).startOf('day');
      const newStart = dayjs(args.newStart.toString()).startOf('day');
  
      if (newStart.isBefore(originalStart)) {
        message.warning('원래 시작일보다 이전으로 변경할 수 없습니다.');
        args.preventDefault();
        return;
      }
      const exclusiveEndInstant = args.newEnd?.toString();
      if (!exclusiveEndInstant) {
        args.preventDefault();
        return;
      }
      const exclusiveEndDate = dayjs(exclusiveEndInstant).format('YYYY-MM-DD');
      const newEndDate = dayjs(exclusiveEndInstant).subtract(1, 'day').format('YYYY-MM-DD');
      const newStartStr = newStart.format('YYYY-MM-DD');
      setEvents(prev => prev.map(ev => ev.id === eventId
        ? { ...ev, start: newStartStr, end: exclusiveEndDate, endDate: newEndDate }
        : ev));
      markEventDirty(eventId, { start: newStartStr, endDate: newEndDate });
      message.success('일정 이동이 저장 대기 상태입니다. 캘린더 저장 버튼을 눌러 반영하세요.');
    }, [markEventDirty, message]);
  
    const handleEventResize = useCallback((args: DayPilot.SchedulerEventResizeArgs) => {
      const eventId = String(args.e.id());
      
      // 시작일 변경 감지
      const oldStart = dayjs(args.e.start().toString()).startOf('day');
      const newStart = dayjs(args.newStart.toString()).startOf('day');
      const isStartChanged = !newStart.isSame(oldStart);
  
      if (isStartChanged) {
        const baseline = baselineEventsRef.current[eventId];
        const originalStart = baseline ? dayjs(baseline.start).startOf('day') : oldStart;
        if (newStart.isBefore(originalStart)) {
          message.warning('원래 시작일보다 이전으로 변경할 수 없습니다.');
          args.preventDefault();
          return;
        }
      }
  
      const exclusiveEndInstant = args.newEnd?.toString();
      if (!exclusiveEndInstant) {
        args.preventDefault();
        return;
      }
      const exclusiveEndDate = dayjs(exclusiveEndInstant).format('YYYY-MM-DD');
      const newEndDate = dayjs(exclusiveEndInstant).subtract(1, 'day');
      
      // 시작일이 종료일보다 늦어지는 경우 방지 (최소 1일 유지)
      if (newEndDate.isBefore(newStart)) {
        message.warning('종료일은 시작일 이전일 수 없습니다.');
        args.preventDefault();
        return;
      }
  
      const newStartStr = newStart.format('YYYY-MM-DD');
      const newEndDateStr = newEndDate.format('YYYY-MM-DD');
  
      setEvents(prev => prev.map(ev => ev.id === eventId
        ? { ...ev, start: newStartStr, end: exclusiveEndDate, endDate: newEndDateStr }
        : ev));
  
      const dirtyUpdates: { start?: string; endDate?: string } = { endDate: newEndDateStr };
      if (isStartChanged) {
        dirtyUpdates.start = newStartStr;
      }
      markEventDirty(eventId, dirtyUpdates);
  
      message.success('일정 기간 변경이 저장 대기 상태입니다. 캘린더 저장 버튼을 눌러 반영하세요.');
    }, [markEventDirty, message]);
  
    const handleEventClick = useCallback((args: DayPilot.SchedulerEventClickArgs) => {
      navigate(`/admin/learning/detail/${args.e.id()}`);
    }, [navigate]);

    const handleEventRightClick = useCallback((args: DayPilot.SchedulerEventRightClickArgs) => {
      args.preventDefault();
      openCopyModalForEvent(String(args.e.id()));
    }, [openCopyModalForEvent]);

  const handleBeforeRowHeaderRender = useCallback((args: DayPilot.SchedulerBeforeRowHeaderRenderArgs) => {
    if (args.row.index % 2 === 0) {
      args.row.backColor = '#F3F4F6';
    } else {
      args.row.backColor = '#FFFFFF';
    }
  }, []);

  const handleBeforeCellRender = useCallback((args: DayPilot.SchedulerBeforeCellRenderArgs) => {
    const rowIndex = args.cell.row?.index ?? 0;
    const cssTokens: string[] = [];
    if (rowIndex % 2 === 0) {
      args.cell.properties.backColor = '#F9FAFB';
    } else {
      args.cell.properties.backColor = '#FFFFFF';
    }

    const cellDate = dayjs(args.cell.start ? args.cell.start.toDate() : undefined);
    if (cellDate.isValid()) {
      const isWeekend = [0, 6].includes(cellDate.day());
      const isToday = cellDate.isSame(todayRef, 'day');
      const isMonthBoundary = cellDate.date() === 1;

      if (isWeekend) {
        cssTokens.push('scheduler-cell-weekend');
      }
      if (isMonthBoundary) {
        cssTokens.push('scheduler-cell-month-boundary');
      }
      if (isToday) {
        cssTokens.push('scheduler-cell-today');
      }
    }

    if (cssTokens.length) {
      const existing = args.cell.properties.cssClass;
      args.cell.properties.cssClass = existing
        ? `${existing} ${cssTokens.join(' ')}`
        : cssTokens.join(' ');
    }
  }, [todayRef]);

  const handleBeforeTimeHeaderRender = useCallback((args: DayPilot.SchedulerBeforeTimeHeaderRenderArgs) => {
    const headerLevel = args.header.level ?? 0;
    const headerDate = dayjs(args.header.start ? args.header.start.toDate() : undefined);

    if (headerLevel === 0) {
      args.header.html = headerDate.isValid()
        ? `<div class="scheduler-header-month">${headerDate.format('YYYY년 M월')}</div>`
        : args.header.html;
      return;
    }

    if (!headerDate.isValid()) return;

    const cssClasses = ['scheduler-header-day'];
    const weekday = headerDate.day();
    const isWeekend = weekday === 0 || weekday === 6;
    const isToday = headerDate.isSame(todayRef, 'day');

    if (isWeekend) {
      cssClasses.push('scheduler-header-day--weekend');
    }
    if (isToday) {
      cssClasses.push('scheduler-header-day--today');
    }

    args.header.html = `
      <div class="${cssClasses.join(' ')}">
        <span class="scheduler-header-day__date">${headerDate.format('D')}</span>
      </div>
    `;
  }, [todayRef]);

  // 학원 옵션 로드
  useEffect(() => {
    (async () => {
      try {
        const res = await adminAcademyApi.list({ q: '', contractStatus: undefined, page: 0, size: 100 });
        const data = res?.data || res;
        const page = data?.data || data;
        const content = page?.content || [];
        const opts: AcademyOption[] = content.map((a: any) => ({
          value: a.code || a.academyCode || String(a.id || a.academyName || ''),
          label: a.name || a.academyName,
          academyId: a.id || a.academyId,
          academyCode: a.code || a.academyCode || String(a.id || '')
        }));
        setAcademyOptions([{ value: 'all', label: '전체' }, ...opts]);
        const alpha = opts.find((o: AcademyOption) => String(o.label || '').includes('알파'));
        setSelectedAcademy(alpha?.value || 'all');
      } catch (e) {
        message.error('학원 목록을 불러오지 못했습니다.');
      }
    })();
  }, []);

  const [appliedSearchText, setAppliedSearchText] = useState<string>('');
  
  // 학원 선택에 따라 실제 학생 목록 로드 (서버 사이드 페이징 적용)
  useEffect(() => {
    (async () => {
      try {
        const params: any = {
          page: page - 1,
          size: pageSize,
          q: appliedSearchText || undefined,
        };

        if (selectedAcademy !== 'all') {
          params.academyCode = selectedAcademy;
        }

        const res = await adminStudentApi.listAll(params);
        const data = res?.data || res;
        const content = data?.content || [];
        const totalElements = data?.totalElements || 0;

        const items: Student[] = content.map((s: any) => ({
          id: String(s.userId), // userId를 메인 식별자로 사용
          name: s.name,
          birthDate: s.birthDate || '',
          academy: s.academyCode || '',
          academyName: s.academyName || '',
          loginId: s.studentLoginId || ''
        }));

        const initialResources = generateResources(items);
        setResources(initialResources);
        setFilteredResources(initialResources);
        setTotalResources(totalElements);
        
        // 기존 이벤트 초기화
        setEvents([]);
        setFilteredEvents([]);
        setResourcesVersion(prev => prev + 1);
      } catch (e) {
        console.error(e);
        message.error('학생 목록을 불러오지 못했습니다.');
      }
    })();
  }, [selectedAcademy, page, pageSize, appliedSearchText]); 

  // 검색어 변경 시 페이지 초기화
  const handleSearch = () => {
    setPage(1);
    setAppliedSearchText(searchText);
  };

  // 샘플 학습 이벤트 데이터
  /* const sampleEvents: LearningEvent[] = [
    {
      id: 'event1',
      title: '신년 첫 수업',
      start: '2025-01-02',
      end: '2025-01-02',
      resourceId: 'student1',
      studentName: '김철수',
      birthDate: '2010-03-15',
      backgroundColor: '#1890ff',
      borderColor: '#1890ff'
    },
    {
      id: 'event2',
      title: '겨울방학 특강',
      start: '2025-01-02',
      end: '2025-01-02',
      resourceId: 'student2',
      studentName: '이영희',
      birthDate: '2009-07-22',
      backgroundColor: '#52c41a',
      borderColor: '#52c41a'
    },
    {
      id: 'event3',
      title: '영어 캠프',
      start: '2025-01-03',
      end: '2025-01-03',
      resourceId: 'student3',
      studentName: '박민수',
      birthDate: '2011-01-08',
      backgroundColor: '#faad14',
      borderColor: '#faad14'
    },
    {
      id: 'event4',
      title: '토익 준비반',
      start: '2025-01-03',
      end: '2025-01-03',
      resourceId: 'student4',
      studentName: '정수진',
      birthDate: '2010-11-30',
      backgroundColor: '#f5222d',
      borderColor: '#f5222d'
    },
    {
      id: 'event5',
      title: '토플 준비반',
      start: '2025-01-04',
      end: '2025-01-04',
      resourceId: 'student5',
      studentName: '최동현',
      birthDate: '2009-05-12',
      backgroundColor: '#722ed1',
      borderColor: '#722ed1'
    },
    {
      id: 'event6',
      title: '발렌타인 특별 수업',
      start: '2025-02-14',
      end: '2025-02-14',
      resourceId: 'student1',
      studentName: '김철수',
      birthDate: '2010-03-15',
      backgroundColor: '#1890ff',
      borderColor: '#1890ff'
    },
    {
      id: 'event7',
      title: '로맨틱 영어',
      start: '2025-02-14',
      end: '2025-02-14',
      resourceId: 'student2',
      studentName: '이영희',
      birthDate: '2009-07-22',
      backgroundColor: '#52c41a',
      borderColor: '#52c41a'
    },
    {
      id: 'event8',
      title: '신학기 준비 수업',
      start: '2025-03-01',
      end: '2025-03-01',
      resourceId: 'student3',
      studentName: '박민수',
      birthDate: '2011-01-08',
      backgroundColor: '#faad14',
      borderColor: '#faad14'
    },
    {
      id: 'event9',
      title: '입학식 영어',
      start: '2025-03-01',
      end: '2025-03-01',
      resourceId: 'student4',
      studentName: '정수진',
      birthDate: '2010-11-30',
      backgroundColor: '#f5222d',
      borderColor: '#f5222d'
    },
    {
      id: 'event10',
      title: '신입생 오리엔테이션',
      start: '2025-03-02',
      end: '2025-03-02',
      resourceId: 'student5',
      studentName: '최동현',
      birthDate: '2009-05-12',
      backgroundColor: '#722ed1',
      borderColor: '#722ed1'
    }
  ]; */



  // 리소스 데이터 생성(이름 / 생년월일 표시)
const generateResources = (studentList: Student[]): Resource[] => {
  return studentList.map(student => {
    const displayId = student.loginId || student.id;
    const academyLabel = student.academyName ? ` (${student.academyName})` : '';
    return {
      id: student.id,
      title: `${student.name} / ${displayId}${academyLabel}`,
      extendedProps: {
        birthDate: student.birthDate,
        academy: student.academy,
        academyName: student.academyName,
        loginId: displayId
      }
    };
  });
};

  const handleSaveChanges = useCallback(async () => {
    if (!hasPendingChanges) {
      message.info('저장할 변경이 없습니다.');
      return;
    }
    setIsSaving(true);
    try {
      for (const [eventId, change] of Object.entries(pendingAdjustments)) {
        const baseline = baselineEventsRef.current[eventId];
        if (!baseline) continue;
        if (change.start && change.start !== baseline.start) {
          const deltaDays = dayjs(change.start).diff(dayjs(baseline.start), 'day');
          if (deltaDays !== 0) {
            await adminLearningScheduleApi.adjust(eventId, { mode: 'drag', deltaDays });
          }
        }
        if (change.endDate && change.endDate !== baseline.endDate) {
          await adminLearningScheduleApi.adjust(eventId, { mode: 'resize', newEndDate: change.endDate });
        }
      }

      for (const copy of pendingCopies) {
        if (!copy.targetStudentUserIds.length) continue;
        await adminLearningScheduleApi.copyToStudents(copy.sourceScheduleId, { targetStudentUserIds: copy.targetStudentUserIds });
      }

      message.success('캘린더 변경 사항이 저장되었습니다.');
      setScheduleReloadSeq(prev => prev + 1);
      setPendingAdjustments({});
      setPendingCopies([]);
    } catch (error) {
      console.error('캘린더 저장 실패:', error);
      message.error('캘린더 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [hasPendingChanges, pendingAdjustments, pendingCopies]);

  // 초기에는 전체 학원 기준으로 옵션만 로드되고 학생은 위 effect에서 로드

  // 학원/검색 필터 적용(AND 검색) - 로컬 필터 제거하고 서버 사이드 검색 사용
  // filteredResources는 이제 resources와 동일하게 유지 (useEffect에서 설정)
  useEffect(() => {
     // 이벤트 필터링만 수행
     const allowedIds = new Set(resources.map(r => r.id));
     setFilteredEvents(events.filter(e => allowedIds.has(e.resourceId)));
  }, [resources, events]);

  // 스케줄 로드: 학원/기간 변경 시 호출
  useEffect(() => {
    if (!resourcesVersion) return;
    const loadSchedules = async () => {
      try {
        const targetAcademies = selectedAcademy === 'all'
          ? academyOptions.filter(opt => opt.value !== 'all')
          : academyOptions.filter(opt => opt.value === selectedAcademy);

        if (!targetAcademies.length) {
          setEvents([]);
          setFilteredEvents([]);
          return;
        }

        const combinedEvents: LearningEvent[] = [];

        for (const academy of targetAcademies) {
          const params: any = { start: rangeStart, end: rangeEnd };
          if (academy.academyId !== undefined && academy.academyId !== null) {
            params.academyId = academy.academyId;
          } else if (academy.academyCode) {
            params.academyCode = academy.academyCode;
          } else if (academy.value) {
            params.academyCode = academy.value;
          }
          if (!params.academyId && !params.academyCode) {
            continue;
          }
          try {
            const res = await adminLearningScheduleApi.list(params);
        const data = res?.data || res;
        const payload = data?.events || data?.data?.events || [];
            payload.forEach((ev: any) => {
              const endSource = ev.end || ev.endDate || ev.start;
              const endDayjs = dayjs(endSource);
              const actualEndDate = endDayjs.isValid() ? endDayjs.format('YYYY-MM-DD') : String(endSource || '');
              const calendarEnd = endDayjs.isValid()
                ? endDayjs.add(1, 'day').format('YYYY-MM-DD')
                : dayjs(ev.start).add(1, 'day').format('YYYY-MM-DD');
              combinedEvents.push({
          id: String(ev.id),
          title: ev.title || '학습',
          start: String(ev.start),
          endDate: actualEndDate,
                end: calendarEnd,
          resourceId: String(ev.studentUserId ?? ev.resourceId ?? ''),
          studentName: '',
          birthDate: '',
                academyId: academy.academyId ?? null,
                academyCode: academy.academyCode || academy.value,
          backgroundColor: ev.backgroundColor || '#1890ff',
                borderColor: ev.borderColor || ev.backgroundColor || '#1890ff',
                groupUuid: ev.groupUuid || ev.group_uuid || '',
                extendedProps: {
                  academyId: academy.academyId ?? null,
                  academyCode: academy.academyCode || academy.value,
                  actualEndDate,
                },
              });
            });
          } catch (_) {
            // 개별 학원 조회 실패는 무시
          }
        }

        setEvents(combinedEvents);
        baselineEventsRef.current = combinedEvents.reduce<Record<string, { start: string; endDate: string }>>((acc, ev) => {
          acc[ev.id] = { start: ev.start, endDate: ev.endDate };
          return acc;
        }, {});
        setPendingAdjustments({});
        setPendingCopies([]);

        // [Pagination] 현재 페이지의 학생에 대한 이벤트만 필터링하여 표시하므로, 
        // 화면에 없는 학생(missingIds)을 추가로 로드하는 로직은 제거함.
        // 사용자는 페이지를 넘겨서 해당 학생을 찾아야 함.
      } catch (e) {
        // silent fail
      }
    };

    loadSchedules();
  }, [selectedAcademy, rangeStart, rangeEnd, academyOptions, resourcesVersion, scheduleReloadSeq]);





  if (view === 'grading') {
    return <AdminLearningGradingList />;
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle" gutter={16}>
            <Col>
              <Title level={2}>학습관리</Title>
            </Col>
            <Col>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {hasPendingChanges && (
                  <span style={{ color: '#666', fontSize: 14 }}>
                    저장 대기:
                    {pendingAdjustmentCount > 0 && ` 일정 조정 ${pendingAdjustmentCount}건`}
                    {pendingAdjustmentCount > 0 && pendingCopyCount > 0 && ' ·'}
                    {pendingCopyCount > 0 && ` 복사 ${pendingCopyCount}명`}
                  </span>
                )}
                <Button
                  type="primary"
                  disabled={!hasPendingChanges || isSaving}
                  loading={isSaving}
                  onClick={handleSaveChanges}
                >
                  {isSaving ? '저장 중...' : '캘린더 저장'}
                </Button>
                <Button 
                  type="primary" 
                  onClick={() => navigate('/admin/learning/register')}
                >
                  학습 등록
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* 검색 영역 */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <label className="block mb-2 text-sm font-medium">학원</label>
              <Select
                placeholder="학원을 선택하세요"
                value={selectedAcademy}
                onChange={setSelectedAcademy}
                allowClear
                style={{ width: '100%' }}
                size="large"
              >
                {academyOptions.map(academy => (
                  <Option key={academy.value} value={academy.value}>
                    {academy.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <label className="block mb-2 text-sm font-medium">학생 검색</label>
              <Input
                placeholder="학생 ID 또는 이름으로 검색 (학원 AND 검색)"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined onClick={handleSearch} />}
                size="large"
                allowClear
              />
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', paddingTop: '32px' }}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={totalResources}
                  onChange={(p, s) => {
                    setPage(p);
                    setPageSize(s);
                  }}
                  showSizeChanger
                  pageSizeOptions={['20', '50', '100', '200']}
                />
                <span style={{ color: '#666', fontSize: '14px' }}>
                  총 {totalResources}명의 학생
                </span>
              </div>
            </Col>
          </Row>
        </Card>

        {/* DayPilot 스케줄러 */}
        <Card>
          <div>
            <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
              <div className='flex gap-2'>
                <Button onClick={() => handleShiftRange(-1)}>이전</Button>
                <Button onClick={handleToday}>오늘</Button>
                <Button onClick={() => handleShiftRange(1)}>다음</Button>
              </div>
              <div className='text-sm text-gray-500'>
                {dayjs(rangeStart).format('YYYY.MM.DD')} ~ {dayjs(rangeEnd).format('YYYY.MM.DD')}
              </div>
              <div className='flex items-center gap-2'>
                <Button type={viewMode === 'month' ? 'primary' : 'default'} onClick={() => handleViewModeChange('month')}>월 단위</Button>
                <Button type={viewMode === 'year' ? 'primary' : 'default'} onClick={() => handleViewModeChange('year')}>연 단위</Button>
                <Button
                  aria-label="캘린더 왼쪽 이동"
                  title="왼쪽으로 이동"
                  onClick={() => scrollSchedulerBy(-1)}
                  icon={<LeftOutlined />}
                />
                <Button
                  aria-label="캘린더 오른쪽 이동"
                  title="오른쪽으로 이동"
                  onClick={() => scrollSchedulerBy(1)}
                  icon={<RightOutlined />}
                />
              </div>
            </div>
          <div
            ref={schedulerContainerRef}
            className='learning-scheduler-wrapper relative h-[600px] w-full overflow-auto rounded-2xl border border-gray-200 bg-white shadow-md'
            onMouseDown={handleSchedulerContainerMouseDown}
          >
            <DayPilotScheduler
              controlRef={schedulerRef}
              startDate={rangeStart}
              days={daysInRange}
              scale="Day"
              cellWidth={cellWidth}
              timeHeaders={timeHeaders}
              resources={schedulerResources}
              events={schedulerEvents}
              rowHeaderWidth={rowHeaderWidthRounded}
              heightSpec="Fixed"
              height={600}
              durationBarVisible={false}
              locale="ko-kr"
              theme="scheduler_default"
              eventMoveHandling="Update"
              eventResizeHandling="Update"
              eventRightClickHandling="Enabled"
              eventClickHandling="Enabled"
              eventDeleteHandling="Disabled"
              timeRangeSelectedHandling="Disabled"
              onEventMove={handleEventMove}
              onEventResize={handleEventResize}
              onEventClick={handleEventClick}
              onEventRightClick={handleEventRightClick}
              onBeforeRowHeaderRender={handleBeforeRowHeaderRender}
              onBeforeCellRender={handleBeforeCellRender}
              onBeforeTimeHeaderRender={handleBeforeTimeHeaderRender}
            />
            <div
              className={`scheduler-row-header-resizer ${isRowHeaderResizing ? 'scheduler-row-header-resizer--active' : ''}`}
              style={{ left: `${rowHeaderWidthRounded}px` }}
              onMouseDown={handleRowHeaderResizeStart}
              role="presentation"
              aria-hidden="true"
            >
              <span />
            </div>
          </div>
          </div>
        </Card>

        {/* 복사 모달 */}
        <Modal
          open={copyModalVisible}
          onCancel={() => {
            setCopyModalVisible(false);
            setCopyScheduleId(null);
            setCopyCandidates([]);
            setCopySelected([]);
          }}
          onOk={() => {
            if (!copyScheduleId || copySelected.length === 0) { 
              message.warning('숙제 부여할 학생을 선택해주세요.');
              return; 
            }
            const uniqueTargets = Array.from(
              new Set(
                copySelected
                  .map(id => Number(id))
                  .filter(id => !Number.isNaN(id))
              )
            );
            if (!uniqueTargets.length) {
              message.warning('복사할 학생을 다시 선택해 주세요.');
              return;
            }
            setPendingCopies(prev => {
              const next = [...prev];
              const idx = next.findIndex(item => item.sourceScheduleId === copyScheduleId);
              if (idx >= 0) {
                const merged = new Set(next[idx].targetStudentUserIds);
                uniqueTargets.forEach(id => merged.add(id));
                next[idx] = { ...next[idx], targetStudentUserIds: Array.from(merged) };
              } else {
                next.push({ sourceScheduleId: copyScheduleId, targetStudentUserIds: uniqueTargets });
              }
              return next;
            });
            message.success('복사 요청이 저장 대기 상태로 추가되었습니다. 캘린더 저장 버튼을 눌러 반영하세요.');
            setCopyModalVisible(false);
            setCopyScheduleId(null);
            setCopyCandidates([]);
            setCopySelected([]);
          }}
          title="숙제 복사"
        >
          <div style={{ marginBottom: '16px' }}>
            <p>아래 학생들에게 학습을 복사합니다. 복사할 학생을 선택하세요.</p>
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #eee', padding: 8 }}>
            {copyCandidates.map((resource) => (
              <div key={resource.id} style={{ marginBottom: '8px' }}>
                <Checkbox
                  checked={copySelected.includes(String(resource.id))}
                  onChange={(e) => {
                    const id = String(resource.id);
                    if (e.target.checked) {
                      setCopySelected(prev => [...prev, id]);
                    } else {
                      setCopySelected(prev => prev.filter(x => x !== id));
                    }
                  }}
                >
                  {resource.title}
                </Checkbox>
              </div>
            ))}
          </div>
          <p className='mt-4 text-sm text-gray-500'>
            선택한 학생들은 상단의 캘린더 저장 버튼을 눌렀을 때 실제로 복사됩니다.
          </p>
        </Modal>

      </div>
    </AdminLayout>
  );
};

export default AdminLearningManagement; 


